import { normalizeReportMatrix } from "./keyIssueMatrix";
import {
  FileText,
  MapPin,
  CheckCircle,
  ClipboardList,
  AlertTriangle,
  
  Zap,
  FileCheck,
  HardHat,
  CloudSun,
  Landmark,
  Settings,
} from "lucide-react";

import { ProjectDataForm } from "../components/audit/shared/site-overview";
import { SiteDataForm } from "../components/audit/shared/site-data";
import { CompletedWorksForm } from "../components/audit/punch-list/completed-sample-table";
import { ConstructionProgress } from "../components/audit/punch-list/construction-progress";
import { HSEManagement } from "../components/audit/punch-list/hse-management";
import { KeyActivitiesPerformed } from "../components/audit/punch-list/key-activities-performed";
import { KeyActivitiesPlanned } from "../components/audit/punch-list/key-activities-planned";
import { ThirdPartyActivities } from "../components/audit/punch-list/third-party-activities";
import { SiteObservationsSimple } from "../components/audit/shared/site-observations";
import { LiftingActivities } from "../components/audit/punch-list/mvs-css-lifting";
import { LinesPipesDrainsForm } from "../components/audit/shared/lines-pipes-drains";
import { GridConnectionForm } from "../components/audit/shared/grid-connection-form";
import { PlanningAspectsForm } from "../components/audit/shared/planning-aspects-form";
import { ConstructionAspectsForm } from "../components/audit/shared/construction-aspects-form";
import { ShadingObjectsForm } from "../components/audit/pre-construction/shading-objects-form";
import { LandOwnershipForm } from "../components/audit/pre-construction/land-ownership-form";
import { OperationalAspectsForm } from "../components/audit/shared/operational-aspects-form";
import { OperationalSitePlanningAspectsForm } from "../components/audit/operational/operational-planning-aspects";
import { CombinedConstructionAspectsForm } from "../components/audit/operational/operational-construction-aspects";
import { SiteDataCDMForm } from "../components/audit/punch-list/site-data-cdm";
import { CombinedRiskMatrix } from "../components/audit/punch-list/punch-list-risk-matrix";


export const initialProgressState={
      "pre-construction": {
        "site-overview": 0,
        "site-data": 0,
        "lines-pipes-drain": 0,
        "grid-connection-information": 0,
        "planning-aspects": 0,
        "construction-aspects": 0,
        "shading-objects": 0,
        "land-ownership": 0,
        "overall-site-observations": 0,
      },
      "under-construction": {
        "site-overview": 0,
        "site-data": 0,
        "lines-pipes-drain": 0,
        "operational-aspects": 0,
        "overall-site-observations": 0,
      },
      operational: {
        "site-overview": 0,
        "site-data": 0,
        "lines-pipes-drain": 0,
        "grid-connection-information": 0,
        "planning-aspects": 0,
        "operational-aspects": 0,
        "construction-aspects-operational": 0,
        "overall-site-observations": 0,
      },
      "punch-list": {
        "site-overview": 0,
        "site-data": 0,
        "key-issue-matrix": 0,
        "completed-sample-table": 0,
        "key-activities-performed": 0,
        "key-activities-planned": 0,
        "third-party-activities": 0,
        "construction-progress": 0,
        "lifting-activities": 0,
        "hse-management": 0,
        "overall-site-observations": 0,
      },
    }

export const templateSections = {
  "pre-construction": [
    {
      name: "site-overview",
      title: "Site Overview",
      icon: FileText,
      description:
        "Enter project details, contractor information, and system specifications",
    },
    {
      name: "site-data",
      title: "Site Data",
      icon: MapPin,
      description: "Record site conditions, access, and infrastructure details",
    },
    {
      name: "lines-pipes-drain",
      title: "Data on Lines/Wires and Pipes / Drains",
      icon: MapPin,
      description:
        "Capture details of underground and overhead lines, pipelines, drainage systems, and related site constraints",
    },
    {
      name: "grid-connection-information",
      title: "Grid Connection Information",
      icon: Zap,
      description:
        "Capture grid interconnection details including voltage level, utility conditions, substation equipment, and grid protection aspects",
    },
    {
      name: "planning-aspects",
      title: "Planning Aspects",
      icon: FileCheck,
      description:
        "Capture planning visibility, authority consultations, feedback, and applicable site designations or constraints",
    },
    {
      name: "construction-aspects",
      title: "Construction Aspects",
      icon: HardHat,
      description:
        "Capture construction access, logistics feasibility, ground and traffic risks, site utilities availability, and additional remarks",
    },
    {
      name: "shading-objects",
      title: "Shading Objects",
      icon: CloudSun,
      description:
        "Record presence of shading elements, their dimensions, removability, future growth risks, and additional observations",
    },
    {
      name: "land-ownership",
      title: "Land Ownership",
      icon: Landmark,
      description:
        "Capture land ownership structure, third-party land crossings, access rights for construction and O&M, and any legal or ownership-related risks",
    },
    {
      name: "overall-site-observations",
      title: "Overall Site Observations",
      icon: ClipboardList,
      description: "Provide overall assessment and observations",
    },
  ],

  "under-construction": [
    {
      name: "site-overview",
      title: "Site Overview",
      icon: FileText,
      description:
        "Enter project details, contractor information, and system specifications",
    },
    {
      name: "site-data",
      title: "Site Data",
      icon: MapPin,
      description: "Record site conditions, access, and infrastructure details",
    },

    {
      name: "lines-pipes-drain",
      title: "Data on Lines/Wires and Pipes / Drains",
      icon: MapPin,
      description:
        "Capture details of underground and overhead lines, pipelines, drainage systems, and related site constraints",
    },
    {
      name: "operational-aspects",
      title: "Operational Aspects",
      icon: Settings,
      description:
        "Comprehensive operational review covering civil conditions, mechanical framework integrity, module installation quality, and electrical cable management practices",
    },
    {
      name: "overall-site-observations",
      title: "Overall Site Observations",
      icon: ClipboardList,
      description: "Provide overall assessment and observations",
    },
  ],

  operational: [
    {
      name: "site-overview",
      title: "Site Overview",
      icon: FileText,
      description:
        "Enter project details, contractor information, and system specifications",
    },

    {
      name: "site-data",
      title: "Site Data",
      icon: MapPin,
      description: "Record site conditions, access, and infrastructure details",
    },
    {
      name: "lines-pipes-drain",
      title: "Data on Lines/Wires and Pipes / Drains",
      icon: MapPin,
      description:
        "Capture details of underground and overhead lines, pipelines, drainage systems, and related site constraints",
    },
    {
      name: "grid-connection-information",
      title: "Grid Connection Information",
      icon: Zap,
      description:
        "Capture grid interconnection details including voltage level, utility conditions, substation equipment, and grid protection aspects",
    },
    {
      name: "planning-aspects",
      title: "Planning Aspects",
      icon: FileCheck,
      description:
        "Capture project screening conditions, landscape management requirements, and site planning designations or constraints",
    },

    {
      name: "operational-aspects",
      title: "Operational Aspects",
      icon: Settings,
      description:
        "Comprehensive operational review covering civil conditions, mechanical framework integrity, module installation quality, and electrical cable management practices",
    },
    {
      name: "construction-aspects-operational",
      title: "Construction Aspects",
      icon: HardHat,
      description:
        "Record site observations for operational projects covering civil conditions, mechanical framework quality, module installation compliance, and electrical cable management practices",
    },
    {
      name: "overall-site-observations",
      title: "Overall Site Observations",
      icon: ClipboardList,
      description: "Provide overall assessment and observations",
    },
  ],

  "punch-list": [
    {
      name: "site-overview",
      title: "Site Overview",
      icon: FileText,
      description:
        "Enter project details, contractor information, and system specifications",
    },
    {
      name: "site-data",
      title: "Site Data Including Construction Compound",
      icon: MapPin,
      description: "Record site conditions, access, and infrastructure details",
    },
    {
      name: "key-issue-matrix",
      title: "Key Issue Matrix",
      icon: AlertTriangle,
      description: "Document and track key issues and their status",
    },
    {
      name: "completed-sample-table",
      title: "Completed Sample Table",
      icon: CheckCircle,
      description: "Record and track test samples and results",
    },
    {
      name: "key-activities-performed",
      title: "Key Site Activities Completed",
      icon: CheckCircle,
      description: "Record activities completed during the audit period",
    },
    {
      name: "key-activities-planned",
      title: "Key Site Activities Planned",
      icon: ClipboardList,
      description: "Plan upcoming activities and milestones",
    },
    {
      name: "third-party-activities",
      title: "Third Party activities undertaken",
      icon: FileText,
      description: "Document vendor and third-party activities",
    },
    {
      name: "construction-progress",
      title:
        "Construction progress against contractual milestones, planned milestones, and critical path items",
      icon: CheckCircle,
      description: "Track construction phases and overall project progress",
    },
    {
      name: "lifting-activities",
      title: "Lifting Activities",
      icon: MapPin,
      description:
        "Document delivery logistics and lifting operations for the MVS and CSS",
    },
    {
      name: "hse-management",
      title: "HSE management and incident follow up",
      icon: AlertTriangle,
      description: "Document health, safety, and environmental management",
    },
    {
      name: "overall-site-observations",
      title: "Overall Site Observations",
      icon: ClipboardList,
      description: "Provide overall assessment and observations",
    },
  ],
};

export const templateComponents = {
  "pre-construction": [
    {
      name: "site-overview",
      title: "Site Overview",
      icon: FileText,
      description:
        "Enter site details, contractor information, and system specifications",
      component: ProjectDataForm,
    },
    {
      name: "site-data",
      title: "Site Data",
      icon: MapPin,
      description: "Record site conditions, access, and infrastructure details",
      component: SiteDataForm,
    },

    {
      name: "lines-pipes-drain",
      title: "Data on Lines/Wires and Pipes / Drains",
      icon: MapPin,
      description:
        "Capture details of underground and overhead lines, pipelines, drainage systems, and related site constraints",
      component: LinesPipesDrainsForm,
    },
    {
      name: "grid-connection-information",
      title: "Grid Connection Information",
      icon: Zap,
      description:
        "Capture grid interconnection details including voltage level, utility conditions, substation equipment, and grid protection aspects",
      component: GridConnectionForm,
    },
    {
      name: "planning-aspects",
      title: "Planning Aspects",
      icon: FileCheck,
      description:
        "Capture planning visibility, authority consultations, feedback, and applicable site designations or constraints",
      component: PlanningAspectsForm,
    },
    {
      name: "construction-aspects",
      title: "Construction Aspects",
      icon: HardHat,
      description:
        "Capture construction access, logistics feasibility, ground and traffic risks, site utilities availability, and additional remarks",
      component: ConstructionAspectsForm,
    },
    {
      name: "shading-objects",
      title: "Shading Objects",
      icon: CloudSun,
      description:
        "Record presence of shading elements, their dimensions, removability, future growth risks, and additional observations",
      component: ShadingObjectsForm,
    },
    {
      name: "land-ownership",
      title: "Land Ownership",
      icon: Landmark,
      description:
        "Capture land ownership structure, third-party land crossings, access rights for construction and O&M, and any legal or ownership-related risks",
      component: LandOwnershipForm,
    },
    {
      name: "overall-site-observations",
      title: "Overall Site Observations",
      icon: ClipboardList,
      description: "Provide overall assessment and observations",
      component: SiteObservationsSimple,
    },
  ],

  "under-construction": [
    {
      name: "site-overview",
      title: "Site Overview",
      icon: FileText,
      description:
        "Enter site details, contractor information, and system specifications",
      component: ProjectDataForm,
    },
    {
      name: "site-data",
      title: "Site Data",
      icon: MapPin,
      description: "Record site conditions, access, and infrastructure details",
      component: SiteDataForm,
    },

    {
      name: "lines-pipes-drain",
      title: "Data on Lines/Wires and Pipes / Drains",
      icon: MapPin,
      description:
        "Capture details of underground and overhead lines, pipelines, drainage systems, and related site constraints",
      component: LinesPipesDrainsForm,
    },
    {
      name: "operational-aspects",
      title: "Operational Aspects",
      icon: Settings,
      description:
        "Comprehensive operational review covering civil conditions, mechanical framework integrity, module installation quality, and electrical cable management practices",
      component: OperationalAspectsForm,
    },
    {
      name: "overall-site-observations",
      title: "Overall Site Observations",
      icon: ClipboardList,
      description: "Provide overall assessment and observations",
      component: SiteObservationsSimple,
    },
  ],

  operational: [
    {
      name: "site-overview",
      title: "Site Overview",
      icon: FileText,
      description:
        "Enter site details, contractor information, and system specifications",
      component: ProjectDataForm,
    },
    {
      name: "site-data",
      title: "Site Data",
      icon: MapPin,
      description: "Record site conditions, access, and infrastructure details",
      component: SiteDataForm,
    },

    {
      name: "lines-pipes-drain",
      title: "Data on Lines/Wires and Pipes / Drains",
      icon: MapPin,
      description:
        "Capture details of underground and overhead lines, pipelines, drainage systems, and related site constraints",
      component: LinesPipesDrainsForm,
    },
    {
      name: "grid-connection-information",
      title: "Grid Connection Information",
      icon: Zap,
      description:
        "Capture grid interconnection details including voltage level, utility conditions, substation equipment, and grid protection aspects",
      component: GridConnectionForm,
    },
    {
      name: "planning-aspects",
      title: "Planning Aspects",
      icon: FileCheck,
      description:
        "Capture project screening conditions, landscape management requirements, and site planning designations or constraints",
      component: OperationalSitePlanningAspectsForm,
    },

    {
      name: "operational-aspects",
      title: "Operational Aspects",
      icon: Settings,
      description:
        "Comprehensive operational review covering civil conditions, mechanical framework integrity, module installation quality, and electrical cable management practices",
      component: OperationalAspectsForm,
    },

    {
      name: "construction-aspects-operational",
      title: "Construction Aspects",
      icon: HardHat,
      description:
        "Record site observations for operational projects covering civil conditions, mechanical framework quality, module installation compliance, and electrical cable management practices",
      component: CombinedConstructionAspectsForm,
    },
    {
      name: "overall-site-observations",
      title: "Overall Site Observations",
      icon: ClipboardList,
      description: "Provide overall assessment and observations",
      component: SiteObservationsSimple,
    },
  ],

  "punch-list": [
    {
      name: "site-overview",
      title: "Site Overview",
      icon: FileText,
      description:
        "Enter site details, contractor information, and system specifications",
      component: ProjectDataForm,
    },
    {
      name: "site-data",
      title: "Site Data Including Construction Compound",
      icon: MapPin,
      description: "Record site conditions, access, and infrastructure details",
      component: SiteDataCDMForm,
    },
    {
      name: "key-issue-matrix",
      title: "Key Issue Matrix",
      icon: AlertTriangle,
      description: "Document and track key issues and their status",
      component: CombinedRiskMatrix,
    },
    {
      name: "completed-sample-table",
      title: "Completed Sample Table",
      icon: CheckCircle,
      description: "Record and track test samples and results",
      component: CompletedWorksForm,
    },
    {
      name: "key-activities-performed",
      title: "Key Site Activities Completed",
      icon: CheckCircle,
      description: "Record activities completed during the audit period",
      component: KeyActivitiesPerformed,
    },
    {
      name: "key-activities-planned",
      title: "Key Site Activities Planned",
      icon: ClipboardList,
      description: "Plan upcoming activities and milestones",
      component: KeyActivitiesPlanned,
    },
    {
      name: "third-party-activities",
      title: "Third Party activities undertaken",
      icon: FileText,
      description: "Document vendor and third-party activities",
      component: ThirdPartyActivities,
    },
    {
      name: "construction-progress",
      title:
        "Construction progress against contractual milestones, planned milestones, and critical path items",
      icon: CheckCircle,
      description: "Track construction phases and overall project progress",
      component: ConstructionProgress,
    },
    {
      name: "lifting-activities",
      title: "Lifting Activities",
      icon: MapPin,
      description:
        "Document delivery logistics and lifting operations for the MVS and CSS",
      component: LiftingActivities,
    },

    {
      name: "hse-management",
      title: "HSE management and incident follow up",
      icon: AlertTriangle,
      description: "Document health, safety, and environmental management",
      component: HSEManagement,
    },
    {
      name: "overall-site-observations",
      title: "Overall Site Observations",
      icon: ClipboardList,
      description: "Provide overall assessment and observations",
      component: SiteObservationsSimple,
    },
  ],
};

export const sharedSiteOverviewProgress = (formData, files = []) => {
  
  const fields = [
    "name",
    "country",
    "postcode",
    "town",
    // "projectInformationNotes",
    // "projectManagerContact",
    // "landlordContactDetails",
    "phone",
    // "mobile",
    // "fax",
    "email",
    "moduleType",
    "inverterType",
    "mountingRack",
    // "sizeOfArea",
    // "plantCapacity",
    "exportCapacity",
    "dcCapacity",
    "acCapacity",
    // "systemSpecificationNotes",
    // "additionalNotes",
  ];
  const filledCount = fields.filter((field) => {
    const value = formData[field];

    // Handle arrays (multi-select fields)
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return value !== null && value !== undefined && value !== "";
  }).length;
const hasImage=files?.filter(
            (file) =>
              file?.relatedTo?.entityType === "project" &&
              
              file?.relatedTo?.filterData?.project_images,
          ) || []
  const totalCount = fields.length + 1; // +1 for the image upload field
  const totalfilledCount = filledCount + (hasImage.length > 0 ? 1 : 0); // Count the image upload field if there are images

  return Math.round((totalfilledCount / totalCount) * 100);
};

export const sharedSiteProgress = (formData) => {
  const fields = ["accessToSite", "description"];

  const filledCount = fields.filter((field) => {
    const value = formData[field];

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return value !== null && value !== undefined && value !== "";
  }).length;

  return Math.round((filledCount / fields.length) * 100);
};

export const sharedLinesPipesDrainsProgress = (formData) => {
  const fields = ["infrastructureTypes"];

  const filledCount = fields.filter((field) => {
    const value = formData[field];

    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === "boolean") {
      return true; // booleans always count since they’re explicit
    }
    return value !== null && value !== undefined && value !== "";
  }).length;

  return Math.round((filledCount / fields.length) * 100);
};

export const sharedGridConnectionProgress = (formData) => {
  const fields = ["utilityCompanyConditions"];

  const filledCount = fields.filter((field) => {
    const value = formData[field];

    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === "boolean") {
      return true; // booleans always count since they’re explicit
    }
    return value !== null && value !== undefined && value !== "";
  }).length;

  return Math.round((filledCount / fields.length) * 100);
};

export const sharedPlanningAspectsProgress = (formData) => {
  const fields = ["siteDesignations"];

  const filledCount = fields.filter((field) => {
    const value = formData[field];

    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === "boolean") {
      return true; // booleans always count since they’re explicit
    }
    return value !== null && value !== undefined && value !== "";
  }).length;

  return Math.round((filledCount / fields.length) * 100);
};

export const sharedConstructionAspectsProgress = (formData) => {
  const fields = ["constructionAccessDescription"];

  const filledCount = fields.filter((field) => {
    const value = formData[field];

    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === "boolean") {
      return true; // booleans always count since they’re explicit
    }
    return value !== null && value !== undefined && value !== "";
  }).length;

  return Math.round((filledCount / fields.length) * 100);
};
export const sharedShadingObjectsProgress = (formData) => {
  const fields = ["heightWidth", "other"];

  const filledCount = fields.filter((field) => {
    const value = formData[field];

    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === "boolean") {
      return true; // booleans always count since they’re explicit
    }
    return value !== null && value !== undefined && value !== "";
  }).length;

  return Math.round((filledCount / fields.length) * 100);
};

export const sharedLandOwnershipProgress = (formData) => {
  const fields = ["ownershipType"];

  const filledCount = fields.filter((field) => {
    const value = formData[field];

    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === "boolean") {
      return true; // booleans always count since they’re explicit
    }
    return value !== null && value !== undefined && value !== "";
  }).length;

  return Math.round((filledCount / fields.length) * 100);
};

export const sharedOperationalAspectsProgress = (formData,templateId) => {

  if(templateId==="operational")return 100
  const fields =  [
    "civilSiteAccessRoadCondition",
    // "civilLVCablesManholesCondition",
    // "civilHVCablesManholesCondition",
    // "civilFoundationCondition",

    // "electricalDCMainCableSpecification",
    // "electricalDCMainInstallationMethodology",
    // "electricalDCMainCableJointsCrimpsDies",

    // "otherCableGlandsCondition",
  ];

  const filledCount = fields.filter((field) => {
    const value = formData[field];

    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === "boolean") {
      return true; // booleans always count since they’re explicit
    }
    return value !== null && value !== undefined && value !== "";
  }).length;

  return filledCount > 0 && fields.length > 0 ? Math.round((filledCount / fields.length) * 100) : 0;
};

export const operationalPlanningAspectsProgress = (formData) => {
  // const fields = ["siteDesignations"];
  const fields = [];
  const filledCount = fields.filter((field) => {
    const value = formData[field];

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return value !== null && value !== undefined && value.trim() !== "";
  }).length;

  // return Math.round((filledCount / fields.length) * 100);
  return 100 
};
export const operationalConstructionAspectsProgress = (
  formData = {},
  observations = [],
  files = [],
) => {
  const progressFields = [
    // "civil_conditionOfSiteAccessRoad",
    // "civil_constructionCompoundCondition",
    // "civil_trafficProblemsForResidents",
    // // "civil_separateOffloadingArea",
    // // "civil_vehiclesSuitableForSite",
    // "civil_onsiteTrafficManagement",
    // "civil_lvTrenchCondition",
    // "civil_hvTrenchCondition",
    // "civil_groundConditionProblems",
    // "civil_civilNotes",

    // "mechanical_pilingQuality",
    // "mechanical_rammingHeadProfile",
    // "mechanical_rammingDepth",
    // "mechanical_undergroundSurprises",
    // "mechanical_alignmentTwisted",
    // "mechanical_regalvanizationMethod",
    // "mechanical_drillingConcrete",
    // "mechanical_averageRemainingTime",
    // "mechanical_mountingAngleAdjustment",
    // "mechanical_trafficProblemsForResidents",
    // // "mechanical_separateOffloadingArea",
    // // "mechanical_vehiclesSuitableForSite",
    // "mechanical_onsiteTrafficManagement",
    // "mechanical_lvTrenchCondition",
    // "mechanical_hvTrenchCondition",
    // "mechanical_groundConditionProblems",
    // "mechanical_mechanicalNotes",

    // // Electrical fields
    // "electrical_stringCableType",
    // // "electrical_looseCableEnds",
    // // "electrical_mc4ConnectorSupport",
    // // "electrical_cableSupport",
    // // "electrical_sharpEdgeExposure",
    // // "electrical_cableStringNumbering",
    // // "electrical_conduitManagement",
    // // "electrical_conduitFilledWithFoam",
    // // "electrical_cablesWithoutEndCap",
    // "electrical_cableSpecification",
    // "electrical_cableInstallationMethodology",
    // // "electrical_biMetallicCompatibility",
    // "electrical_cableConnectorsCrimpsDies",
    // "electrical_electricalNotes",

    // // "electrical_stringAboveLooseCableConnector",
    // // "electrical_stringAboveProperSupport",
    // // "electrical_stringAboveNoSharpEdge",
    // // "electrical_stringAboveCableNumbering",
    // // "electrical_stringBelowRoutedThroughConduit",
    // // "electrical_stringBelowConduitSealedWithFoam",
    // // "electrical_dcMainCablesWithoutEndCap",
    // // "electrical_dcMainCableSpecification",
    // // "electrical_dcMainInstallationMethodology",
    // // "electrical_dcMainBimetallicCompatibility",
    // // "electrical_dcMainCableJointsCrimpsDies",

    // // // General
    // // "correctiveActions",
    // // "priorityLevel",

    // // From first component
    // // "moduleInstallationCompliant",
    // "moduleInstallationNotes",
    // // "moduleClampingCompliant",
    // "moduleClampingNotes",
    // // "structureAlignmentCompliant",
    // "structureMisalignmentDetails",
    // "mechanicalStressConcerns",
    // // "cableBendingRadiusCompliant",
    // // "cableTensionCompliant",
    // "cableManagementNotes",
    // // "cableLayingCompliant",
    // // "cableSupportCompliant",
    // // "mc4ConnectorSupportCompliant",
    // "cableSupportNotes",
    // // "cableProtectionCompliant",
    // // "sharpEdgeProtectionCompliant",
    // // "protectionIssuesDetails",
    // // "cableNumberingCompliant",
    // "numberingSystemDescription",
    // "numberingIssuesDetails",

    // // From second component
    // "observationsSummary",
  ];

  const filledFields = progressFields.filter((field) => {
    const value = formData[field];

    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "boolean") return true;

    return value !== null && value !== undefined && value !== "";
  }).length;

  const completedObservations = observations.filter((obs) => {
    const hasTitle = !!obs?.title?.trim();
    const hasDescription = !!obs?.description?.trim();
    const hasImage = files.some(
      (file) => file?.relatedTo?.entityId === obs?.id  || file?.relatedTo?.entityId === obs?._id,
    );
    return hasTitle && hasDescription && hasImage;
  }).length;

  const totalItems = progressFields.length + observations.length;

  if (totalItems === 0) return 0;

  return Math.round(
    ((filledFields + completedObservations) / totalItems) * 100,
  );
};

export const punchListSiteProgress = (formData) => {
  const fields = [
    "topography",
    "topographyNotes",
    "siteAccess",
    "siteAccessNotes",
    "groundConditions",
    "groundConditionsNotes"
  ];
  const filledCount = fields.filter((field) => {
    const value = formData[field];

    if (typeof value === "boolean") {
      return true;
    }

    return value !== null && value !== undefined && value.trim() !== "";
  }).length;

  return Math.round((filledCount / fields.length) * 100);
};

export const punchListRiskMatrixProgress = (risks, files) => {
  try {
    if (risks?.length === 0) return 0;
    const filledRisks =
      risks?.filter((risk) => risk?.title?.trim() && risk?.description?.trim() && files?.some((file) => file?.relatedTo?.entityId === risk?._id))
        ?.length ?? 0;
    return Math.round((filledRisks / risks?.length) * 100);
  } catch (error) {
    console.error("Error calculating progress:", error);
    return 0;
  }
};

export const punchListCompletedSampleTableProgress = (
  works,
  completedWorksDescription,
) => {
  if ((!works || works.length === 0) && !completedWorksDescription) return 0;

  let completedCount = 0;
  works.forEach((work) => {
    const hasDescription = !!work.description?.trim();
    const hasComments = !!work.comments?.trim();
    

    
    if (hasDescription && hasComments ) {
      completedCount++;
    }
  });
  if (completedWorksDescription && completedWorksDescription.trim() !== "") {
    completedCount++;
  }
  return Math.round((completedCount / ((works?.length || 0) + 1)) * 100);
};

// Punch List Progress Calculations
export const punchListKeyActivitiesPerformedProgress = (works, overallSummary,data) => {
 
  if ((!works || works.length === 0) && !overallSummary) return 0;

  let completedCount = 0;

  works.forEach((work) => {
    const hasName = !!work.name?.trim();
    const hasComments = !!work.comments?.trim();

    if (hasName && hasComments ) {
      completedCount++;
    }
  });

  if (overallSummary && overallSummary.trim() !== "") {
    completedCount++;
  }

  return Math.round((completedCount / (works.length + 1)) * 100);
};

export const punchListKeyActivitiesPlannedProgress = (works, overallSummary) =>
{
  if ((!works || works.length === 0) && !overallSummary) return 0;

  let completedCount = 0;

  works.forEach((work) => {
    const hasDescription = !!work.description?.trim();
    const hasComments = !!work.comments?.trim();


    if (hasDescription && hasComments ) {
      completedCount++;
    }
  });

  if (overallSummary && overallSummary.trim() !== "") {
    completedCount++;
  }

  return Math.round((completedCount / (works.length + 1)) * 100);
}



export const punchListThirdPartyActivitiesProgress = (works, overallSummary,files) => {
    if ((!works || works.length === 0) && !overallSummary) return 0;

  let completedCount = 0;
  works.forEach((work) => {
    const hasName= !!work.activityName?.trim();
    const hasStatus= !!work.status?.trim();
    const hasDescription = !!work.description?.trim();
    const hasComments = !!work.comments?.trim();
    const hasFiles = files?.some((file) => file.relatedTo?.entityId === work.id || file.relatedTo?.entityId === work._id);
   
    if (hasName && hasStatus && hasDescription && hasComments && hasFiles) {
      completedCount++;
    }
  });

  if (overallSummary && overallSummary.trim() !== "") {
    completedCount++;
  }

  return Math.round((completedCount / (works.length + 1)) * 100);
};

export const punchListConstructionProgress = (works,overallSummary,files) => {
  if ((!works || works.length === 0) && !overallSummary) return 0;

  let completedCount = 0;

  works.forEach((work) => {
    // const isComplete = work.percentComplete === 100;
    // const hasBottlenecks = !!work.bottlenecks?.trim();
    // const hasPhaseName= !!work.name?.trim();
    // const hasStatus= !!work.status
    // const hasDescription = !!work.description?.trim();
    // const hasComments = !!work.comments?.trim();

    // const hasFiles = files?.some(
    //   (file) =>
    //     file.relatedTo?.entityId === work.id ||
    //     file.relatedTo?.entityId === work._id,
    // );

    // if (hasPhaseName && hasStatus && hasDescription && hasComments && isComplete && hasBottlenecks && hasFiles) {
    //   completedCount++;
    // }
    const hasStatus= work.status;
    if(hasStatus==="completed")completedCount++;
  });

 

  // if (overallSummary && overallSummary.trim() !== "") {
  //   completedCount++;
  // }

  // return Math.round((completedCount / (works.length + 1)) * 100);
  return completedCount && works.length > 0 ? Math.round((completedCount / (works.length)) * 100) : 0;
};

export const punchListMVSCSSLiftingProgress = (works, summary, files) => {
  if ((!works || works.length === 0) && !summary) return 0;

  let completedCount = 0;

  works.forEach((work) => {
    const hasEquipmentUsed = !!work.equipmentUsed?.trim();
    const hasWeight = !!work.weight?.trim();
    const hasCrewLead = !!work.crewLead?.trim();
    const hasLiftingSequence =
      work.liftingSequence !== undefined && work.liftingSequence !== null;
    const hasChecklist = work.checklist && work.checklist.length > 0;

    const hasDescription = !!work.description?.trim();
    const safetyComplete = !!work.safetyChecksCompleted;
    const hasComments = !!work.comments?.trim();
    const hasFiles = files?.some(
      (file) =>
        file.relatedTo?.entityId === work.id ||
        file.relatedTo?.entityId === work._id,
    );

    if (
      hasEquipmentUsed &&
      hasWeight &&
      hasCrewLead &&
      hasLiftingSequence &&
      hasChecklist &&
      hasDescription &&
      safetyComplete &&
      hasComments &&
      hasFiles
    ) {
      completedCount++;
    }
  });

  if (summary && summary.trim() !== "") {
    completedCount++;
  }

  return Math.round((completedCount / (works.length + 1)) * 100);
};

export const punchListHSEManagementProgress = (incidents=[], documents=[], hseData,files) => {
  // if (
  //   (!incidents || incidents.length === 0) &&
  //   (!documents || documents.length === 0)
  // )
  //   return 0;

  let completedCount = 0;

  incidents.forEach((incident) => {
    const hasFiles = files?.some(
      (file) =>
        file.relatedTo?.entityId === incident.id ||
        file.relatedTo?.entityId === incident._id,
    );
    if (incident?.incidentType &&  incident.severity && incident.reportedBy && incident.date && incident.description && incident.actionsTaken && incident.investigationStatus && hasFiles) completedCount++;
  });

  documents.forEach((doc) => {
    const hasFiles = files?.some(
      (file) =>
        file.relatedTo?.entityId === doc.id ||
        file.relatedTo?.entityId === doc._id,
    );
    if (doc.documentName && doc.status && doc.version && doc.date && doc.comments && hasFiles) completedCount++;
  });

let hseComplete =
    hseData?.inductionProcess ?1:0 
   hseComplete+=( hseData?.visitorSignInBookChecked?1:0)

   hseComplete+=( hseData?.securityMeasures?.trim() ?1:0 )
   hseComplete+=( hseData?.safetyPlan?.trim() ?1:0)
    

  const total = (incidents?.length || 0) + (documents?.length || 0) + 4;
  return Math.round(((completedCount + hseComplete) / total) * 100);
};

export const sharedObservationsProgress = (observations, summary,files) => {
  if ((!observations || observations.length === 0) && !summary) return 0;

  let completedCount = 0;

  observations.forEach((obs) => {
    const hasTitle = !!obs.title?.trim();
    const hasDescription = !!obs.description?.trim(); // Changed from comments to description
    const comments = !!obs.comments?.trim();

    if (hasTitle && hasDescription && comments) {
      completedCount++;
    }
  });
  // if (summary && summary.trim() !== "") {
  //   completedCount++;
  // }

  return completedCount && observations.length > 0 ? Math.round((completedCount / (observations.length)) * 100) : 0;
};


export const templateOverallProgress=(reportData)=>{
  const templateName = reportData?.report?.templateId;
  const works=reportData?.works || [];
  const observations=reportData?.observations || [];
  const files=reportData?.files || [];
  const overviewData=reportData?.siteOverview || {}
  const siteData={
        topography:
          overviewData?.environmentalData?.siteTopography?.topography || "",
        accessToSite: overviewData?.accessibilityInfo?.accessToSite || "",
        visibilitySplays:
          overviewData?.accessibilityInfo?.visibilitySplays || "",
        accessRoadWidth:
          overviewData?.accessibilityInfo?.accessRoadWidth || "",
        // Description
        description: overviewData?.description || [],
        additionalObservations: overviewData?.additionalObservations || "",
      } || {};
    
    const siteOverviewProgress = sharedSiteOverviewProgress(overviewData?.projectData || {}, files);
    const siteProgress = sharedSiteProgress(siteData);
          const observationsProgress = sharedObservationsProgress(observations?.filter(
            (obs) => obs?.observationType === "general",
          ) || [], overviewData?.punchList?.observationsSummary, files);
  switch (templateName) {
    case "pre-construction":

    const linesPipesDrainsProgress = sharedLinesPipesDrainsProgress(overviewData?.infrastructureData || {});
    const gridConnectionProgress = sharedGridConnectionProgress(overviewData?.gridConnectionData || {});
    const planningAspectsProgress = sharedPlanningAspectsProgress(overviewData?.planningAspectsData || {});
    const constructionAspectsProgress = sharedConstructionAspectsProgress(overviewData?.constructionAspectsData || {});
    const shadingObjectsProgress =  overviewData?.shadingObjectsData?.shadingObjectsPresent === "yes"
            ? sharedShadingObjectsProgress(
                overviewData?.shadingObjectsData || {},
              )
            : overviewData?.shadingObjectsData?.shadingObjectsPresent ===
                "no"
              ? 100
              : 0; 
    const landOwnershipProgress = sharedLandOwnershipProgress(overviewData?.landOwnershipData || {});
    return Math.round(
      (siteProgress +
        siteOverviewProgress +
        linesPipesDrainsProgress +
        gridConnectionProgress +
        planningAspectsProgress +
        constructionAspectsProgress +
        shadingObjectsProgress +
        landOwnershipProgress+ observationsProgress) / 9
    );
    case "under-construction":
      const linesPipesDrainsProgressUC = sharedLinesPipesDrainsProgress(overviewData?.infrastructureData || {});
      const operationalAspectsProgress = sharedOperationalAspectsProgress(overviewData?.operationalAspectsData || {},templateName);
      return Math.round(
        (siteProgress +
          siteOverviewProgress +
          linesPipesDrainsProgressUC +
          operationalAspectsProgress+ observationsProgress) / 5
      );
   case "operational":
    
      const linesPipesDrainsProgressOP = sharedLinesPipesDrainsProgress(overviewData?.infrastructureData || {});
      const gridConnectionProgressOP = sharedGridConnectionProgress(overviewData?.gridConnectionData || {});
      const planningAspectsProgressOP = operationalPlanningAspectsProgress(overviewData?.planningAspectsData || {});
      const operationalAspectsProgressOP = sharedOperationalAspectsProgress(overviewData?.operationalAspectsData || {},templateName);
      const constructionAspectsProgressOP = operationalConstructionAspectsProgress(
        overviewData?.operationalData?.constructionAspects || {},
        observations?.filter((obs) => obs?.observationType === "construction") || [],
        files,
      );
      

      return Math.round(
        (siteProgress +
          siteOverviewProgress +
          linesPipesDrainsProgressOP +
          gridConnectionProgressOP +
          planningAspectsProgressOP +
          operationalAspectsProgressOP +
          constructionAspectsProgressOP + observationsProgress) / 8
      );
    case "punch-list":
      const siteProgressPL = punchListSiteProgress(overviewData?.siteData || {});
      const riskMatrixProgress = punchListRiskMatrixProgress(normalizeReportMatrix(reportData).keyIssueMatrixItems,files);

      const completedSampleTableProgress = punchListCompletedSampleTableProgress(
        works?.filter((work) => work?.workCategory === "completed-works") || [],
        overviewData?.punchList?.completedWorksDescription
      );
      const keyActivitiesPerformedProgress = punchListKeyActivitiesPerformedProgress(
        works?.filter((work) => work?.workCategory === "key-activities-performed") || [],
        overviewData?.punchList?.keyActivitiesPerformed
          ?.overallSummary,overviewData
      );
     
      const keyActivitiesPlannedProgress = punchListKeyActivitiesPlannedProgress(
        works?.filter((work) => work?.workCategory === "key-activities-planned") || [],
        overviewData?.punchList?.keyActivitiesPlanned?.overallSummary
      );
      const thirdPartyActivitiesProgress = punchListThirdPartyActivitiesProgress(
        works?.filter((work) => work?.workCategory === "third-party-activities") || [],
        overviewData?.punchList?.thirdPartyActivities?.overallSummary, files
      );
      const constructionProgress = punchListConstructionProgress(
        works?.filter((work) => work?.workCategory === "construction-progress") || [],
        overviewData?.punchList?.constructionProgress?.overallSummary, files
      );
      const liftingActivitiesProgress = punchListMVSCSSLiftingProgress(
        works?.filter((work) => work?.workCategory === "lifting-activities") || [],
        overviewData?.punchList?.liftingActivities?.summary, files
      );
      const hseManagementProgress = punchListHSEManagementProgress(
        works?.filter((work) => work?.workCategory === "hse-incident") || [],
        works?.filter((work) => work?.documentType === "hse-document-review") || [],
        overviewData?.punchList?.hseSummary || {},files
      );

     

      return Math.round(
        (
          siteOverviewProgress+
          siteProgressPL +
          riskMatrixProgress +
          observationsProgress +
          completedSampleTableProgress +
          keyActivitiesPerformedProgress +
          keyActivitiesPlannedProgress +
          thirdPartyActivitiesProgress +
          constructionProgress +
          liftingActivitiesProgress +
          hseManagementProgress) / 11
      );



    default:
      return 0;
  }

}
